import type { Course, AppSettings, HonorsCapStatus } from '@gpa-scales/shared-types';
import { CourseType, ResidencyStatus, GradeLevel } from '@gpa-scales/shared-types';
import { resolveGradePoints } from './gradeConversion';

export function isHonorsEligible(course: Course, settings: AppSettings): boolean {
  // AP always eligible
  if (course.courseType === CourseType.AP) return true;

  // IB HL always eligible
  if (course.courseType === CourseType.IB_HL) return true;

  // Non-resident restrictions: only AP and IB qualify
  if (settings.residencyStatus === ResidencyStatus.NonResident) {
    if (
      course.courseType === CourseType.Honors ||
      course.courseType === CourseType.DualEnrollment
    ) {
      return false;
    }
  }

  // IB SL: only if UC-certified
  if (course.courseType === CourseType.IB_SL) {
    return course.isUCCertifiedHonors === true;
  }

  // Honors: only if UC-certified
  if (course.courseType === CourseType.Honors) {
    return course.isUCCertifiedHonors === true;
  }

  // Dual Enrollment: only if UC-certified
  if (course.courseType === CourseType.DualEnrollment) {
    return course.isUCCertifiedHonors === true;
  }

  // Standard and College courses never eligible
  return false;
}

function getGradePointsSafe(course: Course, settings: AppSettings): number {
  try {
    const result = resolveGradePoints(course, settings);
    return result ?? 0;
  } catch {
    return 0;
  }
}

export function computeHonorsCapStatus(
  _courses: Course[],
  settings: AppSettings,
  ucEligibleCourses: Course[]
): HonorsCapStatus {
  const TOTAL_CAP = 8;
  const TENTH_GRADE_CAP = 4;

  // Find honors-eligible courses among UC-eligible courses
  const honorsEligible = ucEligibleCourses.filter((c) => isHonorsEligible(c, settings));

  // Sort by grade points descending (highest grades get priority)
  const sorted = [...honorsEligible].sort((a, b) => {
    const pointsA = getGradePointsSafe(a, settings);
    const pointsB = getGradePointsSafe(b, settings);
    return pointsB - pointsA;
  });

  // Apply 10th-grade sub-cap
  const tenthGradeHonors = sorted.filter((c) => c.gradeLevel === GradeLevel.Sophomore);
  const cappedTenth = tenthGradeHonors.slice(0, TENTH_GRADE_CAP);

  // Apply total cap
  const eleventhGradeHonors = sorted.filter((c) => c.gradeLevel === GradeLevel.Junior);
  const allCapped = [...cappedTenth, ...eleventhGradeHonors].slice(0, TOTAL_CAP);

  const tenthGradeUsed = Math.min(tenthGradeHonors.length, TENTH_GRADE_CAP);
  const usedPoints = allCapped.length;

  return {
    totalCap: TOTAL_CAP,
    usedPoints,
    remainingPoints: TOTAL_CAP - usedPoints,
    tenthGradeCap: TENTH_GRADE_CAP,
    tenthGradeUsed,
    tenthGradeRemaining: TENTH_GRADE_CAP - tenthGradeUsed,
  };
}
